import { useEffect } from 'react';

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000; // 5 days in milliseconds
const INACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes of inactivity
const CHECK_INTERVAL = 60 * 60 * 1000; // Check every hour

// Track at module level so it persists across component unmount/remount
const appLoadTime = Date.now();
let lastActivityTime = Date.now();
let hasRefreshed = false;
let mouseMoveTimeout: number | undefined;

const refreshApp = () => {
	if (hasRefreshed) {
		return;
	}
	hasRefreshed = true;
	window.location.reload();
};

const isAppStale = () => {
	const appAge = Date.now() - appLoadTime;
	return appAge >= FIVE_DAYS_MS;
};

const isUserInactive = () => {
	const timeSinceLastActivity = Date.now() - lastActivityTime;
	return timeSinceLastActivity >= INACTIVITY_THRESHOLD;
};

const updateLastActivity = () => {
	lastActivityTime = Date.now();
};

const debouncedMouseMove = () => {
	clearTimeout(mouseMoveTimeout);
	mouseMoveTimeout = window.setTimeout(updateLastActivity, 200);
};

const StaleAppDetector = () => {
	useEffect(() => {
		// Add activity listeners
		const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
		events.forEach((event) => {
			document.addEventListener(event, updateLastActivity);
		});
		document.addEventListener('mousemove', debouncedMouseMove);

		// Handle visibility change (tab switching)
		const handleVisibilityChange = () => {
			if (!document.hidden && isAppStale()) {
				// Tab became visible and app is stale → refresh
				refreshApp();
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);

		// Periodic check for stale app with inactive user
		const checkInterval = setInterval(() => {
			// If app is stale AND (tab visible + user inactive) → refresh
			if (isAppStale() && !document.hidden && isUserInactive()) {
				refreshApp();
			}
		}, CHECK_INTERVAL);

		return () => {
			// Cleanup
			events.forEach((event) => {
				document.removeEventListener(event, updateLastActivity);
			});
			document.removeEventListener('mousemove', debouncedMouseMove);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			clearTimeout(mouseMoveTimeout);
			clearInterval(checkInterval);
		};
	}, []);

	return null; // This component doesn't render anything
};

export default StaleAppDetector;
